import React, { FC, useEffect, useState } from "react";
import "./Table.css";
import Spinner from "../spinner-loader/Spinner";

interface Column {
  header: string;
  accessor: any;
  className?: string;
}

interface Action {
  label: any;
  onClick: (row: any, e: React.MouseEvent) => void;
  className?: string;
}

interface TableProps {
  columns: Column[];
  data: any[];
  actions?: Action[];
  rowsPerPage?: number;
  rounded?: boolean;
  onRowClick?: (row: any) => void;
  showPagination?: boolean;
  loading?: boolean;

  serverSidePagination?: boolean;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
}

const Table: FC<TableProps> = ({
  columns,
  data,
  actions = [],
  rowsPerPage = 15,
  rounded = false,
  onRowClick,
  showPagination = true,
  loading = false,
  serverSidePagination = false,
  totalRecords = 0,
  onPageChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = serverSidePagination
    ? Math.max(1, Math.ceil(totalRecords / rowsPerPage))
    : Math.max(1, Math.ceil(data.length / rowsPerPage));

  useEffect(() => {
    if (!serverSidePagination) {
      setCurrentPage(1);
    }
  }, [data, serverSidePagination]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);

    if (serverSidePagination && onPageChange) {
      onPageChange(page);
    }
  };

  const paginatedData = serverSidePagination
    ? data
    : data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div className="table-container">
      <div className="table-scroll">
        <table className={`custom-table ${rounded ? "rounded" : ""}`}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col.header}</th>
              ))}
              {actions.length > 0 && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {/* 🔹 Loading Spinner */}
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (actions.length ? 1 : 0)}
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  <Spinner size={35} />
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              /* 🔹 Empty State */
              <tr>
                <td
                  colSpan={columns.length + (actions.length ? 1 : 0)}
                  className="table-empty"
                >
                  No data found
                </td>
              </tr>
            ) : (
              /* 🔹 Data Rows */
              paginatedData.map((row, idx) => (
                <tr
                  key={idx}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((col, cIdx) => (
                    <td key={cIdx} className={col.className}>
                      {row[col.accessor]}
                    </td>
                  ))}

                  {actions.length > 0 && (
                    <td className="table-action-area">
                      {actions.map((action, aIdx) => (
                        <button
                          key={aIdx}
                          className={`action-btn ${action.className || ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(row, e);
                          }}
                        >
                          {action.label}
                        </button>
                      ))}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="pagination">
          <span>
            Page {currentPage} of {totalPages}
          </span>

          <div className="pagination-btn">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
